<template>
  <section class="sec-top-intro">
    <div class="box-top-slide">
      <div class="img-top-slide">
        <img src="@/assets/img/column/202603_top.png" />
      </div>
      <div class="img-top-slide_text">
        <h2 class="mb-0 text-right" style="color: #7e7e74;"><small>スキンケア成分の選び方｜</small><br>肌荒れ・美白・保湿まで悩み別に解説【正式名称つき】</h2>
      </div>
    </div>
  </section>

  <section class="column_content">

    <div class="pt-5 mt-5 mb-5">
    <h3 class="mb-3 mt-5">スキンケアの成分、気にした方がいいの？</h3>
      <p class="mb-3">スキンケアを選ぶとき、「成分で選びたい」と思って検索したものの、専門用語が多くてよくわからなかった、という経験はないでしょうか。</p>
      <p class="mb-3">たとえば「ビタミンCがいい」と聞いても、成分表には「L-アスコルビン酸2-グルコシド」など、見慣れない名前で記載されていることが一般的。</p>
      <p class="mb-3">でも実は、<strong>検索される一般的な呼び方と、化粧品に記載される正式名称は異なるケースが多い</strong>のです。</p>
      <p class="mb-3">さらに重要なのは、スキンケア成分は「なんとなく良さそう」で選ぶのではなく、<strong>肌悩みに合わせて選ぶべきもの</strong>であるという点。肌荒れ、くすみ、乾燥など、悩みごとに必要な成分は明確に異なります。</p>
      <p class="mb-3">本記事では、よく検索される成分ワードとその正式名称をセットで整理しながら、悩み別にどの成分を選べばよいのかをわかりやすく解説します。</p>
    </div>
    
    <div class="index_list pt-5 mt-5">
      <p class="index_title mb-3">もくじ</p>
      <a href="#" @click.prevent="scrollTo('#sec1')"><p class="mb-3">肌荒れ・ニキビにおすすめの成分</p></a>
      <a href="#" @click.prevent="scrollTo('#sec2')"><p class="mb-3">美白・透明感を目指す成分</p></a>
      <a href="#" @click.prevent="scrollTo('#sec3')"><p class="mb-3">乾燥対策・保湿に必要な成分</p></a>
      <a href="#" @click.prevent="scrollTo('#sec4')"><p class="mb-3">エイジングケアに注目の成分</p></a>
      <a href="#" @click.prevent="scrollTo('#sec5')"><p class="mb-3">今注目の“発酵・乳酸菌”成分</p></a>
    </div>
    
    <div id="sec1" class="feature-row mt-5">
      <div class="feature-row_item_image w-40">
        <img src="@/assets/img/column/202603_01.png">
      </div>
      <div class="feature-row_item_text feature-row_item_text_right w-60">
        <h3 class="mb-3 mt-5">肌荒れ・ニキビを防ぐためにおすすめの成分</h3>
        <p class="mb-3">肌荒れやニキビが気になるときに重要なのは、<strong>肌をすこやかに保ちながら、コンディションを整える成分</strong>です。刺激の強いケアを重ねるのではなく、まずは肌環境を整えることが大切です。</p>
        <p class="mb-3">代表的な成分が、<strong>グリチルリチン酸（正式名称：グリチルリチン酸ジカリウム）</strong>です。甘草由来の成分で、肌荒れやニキビを防ぐ有効成分としても広く使用されており、「ニキビケア 成分」で検索されることも多いです。</p>
        <p class="mb-3"><strong>エクトイン</strong>は、過酷な環境で生きる微生物が生み出す保護成分です。 肌をうるおいのヴェールで包み込み、乾燥などの外的刺激から守る“守る美容”成分として注目されています。</p>
        <p class="mb-3">さらに近年注目されているのが、<strong>CICA（正式名称：ツボクサエキス）</strong>です。韓国スキンケアをきっかけに認知が広がり、「肌をすこやかに保つ成分」として定着しつつあります。</p>
      </div>
    </div>

    <div class="summary">
      <h3 class="mb-3">成分まとめ</h3>
      <p><strong>グリチルリチン酸（正式名称：グリチルリチン酸ジカリウム）</strong></p>
      <p>・炎症を抑え、肌荒れを防ぐ</p>
      <p><strong>エクトイン</strong></p>
      <p>・乾燥などの外的刺激から守る</p>
      <p><strong>CICA（正式名称：ツボクサエキス）</strong></p>
      <p>・肌を落ち着かせ、ゆらぎにくい状態へ導く</p>
      <p class="mt-3">💡<strong>ポイント</strong></p>
      <p>これらの成分は、いずれも「攻めるケア」ではなく、<strong>肌を穏やかに整えるための土台となる成分</strong>。<br>肌トラブルが続いているときほど、こうした成分を中心に選んで見てください。</p>
    </div>
    
    <div id="sec2" class="feature-row mt-5">
      <div class="feature-row_item_image w-40">
        <img src="@/assets/img/column/202603_02.png">
      </div>
      <div class="feature-row_item_text feature-row_item_text_right w-60">
        <h3 class="mb-3 mt-5">美白・透明感を目指す成分</h3>
        <p class="mb-3">肌のくすみや色ムラが気になる場合は、メラニンの生成や炎症にアプローチし、肌全体のトーンを整える成分を選ぶことが重要。いわゆる美白ケアは、単に白さを目指すのではなく、均一で透明感のある肌状態を整えるケアを指します。</p>
        <p class="mb-3">代表的な成分が、<strong>ビタミンC誘導体（正式名称：L-アスコルビン酸2-グルコシド）</strong>。透明感ケアにおいて定番の成分である。ビタミンCを安定化させた形で、肌をなめらかに整えながら、明るい印象へ導きます。</p>
        <p class="mb-3">さらに、トラネキサム酸も美白ケアにおいて重要な成分の一つ。メラニンの生成に関わる炎症プロセスにアプローチすることで、シミやくすみの原因に働きかけるとされており、「シミ対策 成分」として認知されています。</p>
      </div>
    </div>

    <div class="summary">
      <h3 class="mb-3">成分まとめ</h3>
      <p><strong>ビタミンC誘導体 （正式名称：L-アスコルビン酸2-グルコシド）</strong></p>
      <p>・透明感を高める</p>
      <p>・肌を引き締め、なめらかに整える</p>
      <p><strong>トラネキサム酸</strong></p>
      <p>・メラニン生成に関わる炎症にアプローチ</p>
      <p>・シミ・くすみの原因に働きかける</p>
      <p class="mt-3">💡<strong>ポイント</strong></p>
      <p>これらの成分は、<strong>肌の印象そのものを明るく見せるための“土台づくり”に関わる成分</strong>。<br>日々のケアに取り入れることで、くすみの目立ちにくい均一な肌状態を目指すことができます。</p>
    </div>
    
    <div id="sec3" class="feature-row mt-5">
      <div class="feature-row_item_image w-40">
        <img src="@/assets/img/column/202603_03.png">
      </div>
      <div class="feature-row_item_text feature-row_item_text_right w-60">
        <h3 class="mb-3 mt-5">乾燥対策・保湿に必要な成分</h3>
        <p class="mb-3">肌の乾燥が気になる場合は、単に水分を与えるだけでなく、水分を抱え込み、逃がさない仕組みを整えることが重要である。保湿成分は大きく「水分を保持するもの」と「バリア機能をサポートするもの」に分けられます。</p>
        <p class="mb-3">代表的なのが、<strong>ヒアルロン酸（正式名称：ヒアルロン酸Na）</strong>です。高い保水力を持ち、肌の表面にうるおいをとどめる働きがある。「ヒアルロン酸 保湿」といった検索でも広く認知されている成分。</p>
        <p class="mb-3">さらに重要なのが、<strong>セラミド（例：セラミドNG、セラミドNP、セラミドAP）</strong>。角層の細胞間脂質の主成分であり、外部刺激から肌を守りながら、水分の蒸発を防ぐ役割を担う。乾燥対策においては欠かせない存在です。</p>
        <p class="mb-3">また、スクワランは肌になじみやすい油分として、うるおいを閉じ込める働きを持つ。軽い使用感でありながら、乾燥を防ぐサポート成分として幅広く使われています。</p>
      </div>
    </div>

    <div class="summary">
      <h3 class="mb-3">成分まとめ</h3>
      <p><strong>ヒアルロン酸（正式名称：ヒアルロン酸Na）</strong></p>
      <p>・水分を抱え込み、肌にうるおいを与える</p>
      <p><strong>セラミド（例：セラミドNG/NP/AP）</strong></p>
      <p>・バリア機能をサポートし、水分の蒸発を防ぐ</p>
      <p><strong>スクワラン</strong></p>
      <p>・油分でフタをし、うるおいを閉じ込める</p>
      <p class="mt-3">💡<strong>ポイント</strong></p>
      <p>保湿ケアにおいては、<strong>1つの成分に頼るのではなく、複数の役割を組み合わせる</strong>ことが重要です。<br>水分を与える成分と、それを守る成分をバランスよく取り入れることで、乾燥しにくい肌状態を維持しやすくなります。</p>
    </div>
    
    <div id="sec4" class="feature-row mt-5">
      <div class="feature-row_item_image w-40">
        <img src="@/assets/img/column/202603_04.png">
      </div>
      <div class="feature-row_item_text feature-row_item_text_right w-60">
        <h3 class="mb-3 mt-5">エイジングケアに注目の成分</h3>
        <p class="mb-3">年齢とともに気になりやすいハリ不足や乾燥による小じわには、肌のコンディションを底上げする成分を取り入れることが重要。エイジングケアは一つの悩みに特化するのではなく、複数の要因にアプローチする視点が求められます。</p>
        <p class="mb-3">代表的なのが、<strong>ビタミンE（正式名称：トコフェロール）</strong>で。肌をすこやかに保つためのサポート成分として広く使われており、乾燥や外的ダメージを受けた肌のコンディションを整える働きがあります。</p>
        <p class="mb-3">また、近年注目されているのが、<strong>ペプチド（例：テトラペプチド-30）</strong>。ペプチドはアミノ酸が結合した成分で、肌にハリを与えたり、なめらかに整える目的で使用されることが多いです。</p>
        <p class="mb-3">さらに、フィトスフィンゴシンはセラミドのもととなる成分として知られており、バリア機能をサポートしながら、ハリのある肌状態へ導く役割を担う。</p>
        <p class="mb-3">加えて、<strong>ナイアシンアミド</strong>はビタミンB3の一種で、メラニンの移動を抑える働きが知られており、「美白 成分」や「くすみ ケア」で広く検索されています。加えて、ハリやシワへのアプローチも期待される点が特徴です。</p>
        <p class="mb-3">そして、スキンケア成分として注目されている<strong>フラーレン</strong>。キメを整え、ハリやツヤ感のある肌づくりをサポートするといわれています。</p>
      </div>
    </div>

    <div class="summary">
      <h3 class="mb-3">成分まとめ</h3>
      <p><strong>ビタミンE（正式名称：トコフェロール）</strong></p>
      <p>・肌をすこやかに保ち、コンディションを整える</p>
      <p><strong>ペプチド（例：テトラペプチド-30）</strong></p>
      <p>・ハリを与え、なめらかな肌へ導く</p>
      <p><strong>フィトスフィンゴシン</strong></p>
      <p>・バリア機能をサポートし、ハリのある肌へ</p>
      <p><strong>ナイアシンアミド</strong></p>
      <p>・メラニンの移動を抑え、くすみを防ぐ</p>
      <p><strong>フラーレン</strong></p>
      <p>・ハリ・ツヤにもアプローチ</p>
      <p class="mt-3">💡<strong>ポイント</strong></p>
      <p>エイジングケアは、即効性を求めるよりも、<strong>日々の積み重ねによって肌状態を整えていくケア</strong>。<br>保湿や美白などの基本ケアと組み合わせながら、総合的に取り入れていくことが重要です。</p>
    </div>
    
    <div id="sec5" class="feature-row mt-5">
      <div class="feature-row_item_image w-40">
        <img src="@/assets/img/column/202603_05.png">
      </div>
      <div class="feature-row_item_text feature-row_item_text_right w-60">
        <h3 class="mb-3 mt-5">今注目の“発酵・乳酸菌”成分</h3>
        <p class="mb-3">近年、スキンケアにおいて注目を集めているのが、発酵や乳酸菌由来の成分。韓国スキンケアの影響もあり、「肌環境を整えるケア」として広く認知が広がっています。</p>
        <p class="mb-3">これらの成分の特徴は、単にうるおいを与えるだけでなく、肌のコンディションそのものを整えるアプローチにあります。乾燥や外的刺激によってゆらぎやすくなった肌を、すこやかな状態へ導く役割が期待されています。</p>
        <p class="mb-3">代表的なのが、<strong>乳酸菌（正式名称：乳酸桿菌）</strong>。いわゆる「肌フローラ」に着目した成分として知られ、バリア機能をサポートしながら、ゆらぎにくい肌状態を目指すケアに用いられています。</p>
        <p class="mb-3">また、<strong>豆乳発酵液（正式名称：豆乳発酵液）や、酵母エキス（正式名称：酵母エキス／加水分解酵母）</strong>も、発酵によって得られる成分としてスキンケアに活用されています。これらは角層をやわらかく整え、なめらかな肌状態へ導くサポートを担う。</p>
      </div>
    </div>

    <div class="summary">
      <h3 class="mb-3">成分まとめ</h3>
      <p><strong>乳酸菌（正式名称：乳酸桿菌）</strong></p>
      <p>・肌環境を整え、ゆらぎにくい肌へ導く</p>
      <p><strong>豆乳発酵（正式名称：豆乳発酵液）</strong></p>
      <p>・角層を整え、なめらかな肌へ</p>
      <p><strong>酵母（正式名称：酵母エキス／加水分解酵母）</strong></p>
      <p>・うるおいを与え、肌のコンディションをサポート</p>
      <p class="mt-3">💡<strong>ポイント</strong></p>
      <p>発酵や乳酸菌由来の成分は、<strong>即効的な変化を求めるというよりも、肌状態を底から整えるケア</strong>として取り入れるのが適しています。<br>基本的な保湿やバリアケアと組み合わせることで、その価値をより実感しやすくなるでしょう。</p>
    </div>

    <div class="layer_01  my-5 py-5" style="margin-top: 100px !important;">
      <img src="@/assets/img/column/layer-bg_01.jpg">
      <div class="layer-text">
        <p>美肌へ近づくポイント</p>
        <h3 class="mb-5" style="font-size: 3rem;">肌悩みにあわせて成分を選んでみてください</h3>
        <p class="mb-3">スキンケア成分は、「なんとなく良さそう」で選ぶのではなく、<br>自分の肌悩みに合わせて選ぶことが重要です。</p>
        <p class="mb-3">肌荒れには炎症を抑える成分、くすみには透明感を整える成分、乾燥には保湿とバリア機能を支える成分といったように、それぞれ役割が異なります。</p>
        <p class="mb-3">さらに、発酵や乳酸菌といった成分は、肌のコンディションそのものを整えるベースとして取り入れることで、より安定した状態を目指すことができます。</p>
        <p class="mb-3">また、検索でよく目にする成分名と、実際の化粧品に記載されている正式名称が異なることも多いため、名称と役割の両方を理解しておくことが、自分に合ったスキンケア選びにつながる。</p>
        <p class="mb-3">重要なのは、一つの成分に頼るのではなく、複数の成分をバランスよく取り入れること。</p>
        <p class="mb-3">日々のケアの中で、自分の肌状態に合った成分を選び、積み重ねていくことが、理想的な肌状態への近道です。</p>
      </div>
    </div>
    
  </section>


  <!-- トップに戻るボタン -->
  <button id="page-top" class="btn" v-on:click="scrollTop">
    TOP
  </button>

</template>

<style scoped>
.column_content h2,.column_content h3 {
  font-weight: 500 !important;
}
small {
  font-size: .7em;
}
ul {
  list-style: disc;
  margin-left: 25px;
}

.feature-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 50px;
}
.feature-row_item_image img {
  width: 100%;
}
.feature-row_item_text h3 {
  font-size: 1.25em;
}

button#page-top {
  position: fixed;
  right: 20px;
  bottom: 20px;
  z-index: 100;
}


.layer_01 {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background-image: url('../../assets/img/column/layer-bg_01.jpg');
  background-size: cover;
  background-position: center;
  min-height: 500px; /* 最小高さを設定しておくと、極端に小さくなるのを防げます */
  padding: 20px; /* テキストと画像の端の間に余白を設定 */
}
.layer_01 img {
  display: none; /* 画像は背景として使用するので非表示にします */
}
.layer_01 .layer-text {
  /* padding: 10px; */
  text-align: center;
  max-width: 90%; /* 文字が画像からはみ出さないように最大幅を設定 */
  word-wrap: break-word; /* 長い単語が折り返されるようにする */
  color: #424242;
  font-weight: 500;
}
.layer_01 .layer-text .fs-2 {
  font-size: 2em;
}

.feature-row_product_list {
  align-items: inherit;
  justify-content: flex-start;
}
.feature-row_column_list {
  align-items: inherit;
  justify-content: center;
}
.feature-row_product_item {
  width: 25%;
  padding: 5px;
  text-align: center;
}
.feature-row_column_item {
  width: 33%;
  padding: 5px;
  text-align: center;
}
.feature-row_product_detail {
  color: #333;
}
.feature-row_product_cate {
  font-size: .8em;
  color: #6b6b67;
  opacity: .65;
}
.feature-row_voice_list {
  align-items: baseline;
}
.feature-row_voice_list path.a {
  fill: none;
  stroke: #000;
  stroke-width: 2px;
}
.feature-row_voice_list .icon svg {
  width: 70px;
}
.feature-row_voice_list .feature-row_voice_item {
  width: 33%;
  padding: 5px;
  text-align: center;
}

.box-top-slide {
  position: relative;
}
.img-top-slide img {
  width: 100%;
  height: 422px;
  object-fit: cover;
  object-position: left;
}
.img-top-slide_text {
  position: absolute;
  bottom: 30px;
  right: 30px;
  color: #fff;
}
.img-top-slide_text h2 {
  font-size: 2em;
  font-weight: 500 !important;
}
.img-top-slide_text h2 span {
  font-size: .6em;
}

.layer_02 .layer-text,.layer_03 .layer-text,.layer_04 .layer-text {
  background-color: #cacaca;
  padding: 10px;
}
.layer_02 .layer-text .layer-text-inner,.layer_03 .layer-text .layer-text-inner,.layer_04 .layer-text .layer-text-inner {
  background-color: #fff;
  padding: 15px;
}
.layer_02 .layer-text .layer-text-inner h2 span,.layer_03 .layer-text .layer-text-inner h2 span,.layer_04 .layer-text .layer-text-inner h2 span {
  font-size: .6em;
}

/* もくじ */
.index_list {
  border: 2px solid #e6c87a;
  padding: 20px;
  max-width: 600px;
  margin: 0 auto;
}

.index_list a {
  color: #333;
  text-decoration: none;
  border-bottom: 1px dotted #e6c87a;
  padding-bottom: 2px;
}

.index_list a:hover {
  color: #e6c87a;
}

.index_list a p {
  border-bottom: 1px dotted #e6c87a;
}

.index_title {
  border-bottom: 1px solid #e6c87a;
}

.summary {
  width: 80%;
  margin: 0 auto;
}

@media screen and (min-width: 991px) {
    .img-top-slide img {
    object-fit: contain;
    }
    .layer_02 {
      position: relative;
      display: flex;
      align-items: flex-start;
      background-image: url('../../assets/img/column/202501_05.jpg');
      background-size: cover;
      min-height: 600px;
      padding: 60px;
    }
    .layer_03 {
      position: relative;
      display: flex;
      align-items: flex-start;
      background-image: url('../../assets/img/column/202501_06.jpg');
      background-size: cover;
      min-height: 600px;
      padding: 60px;
    }
    .layer_04 {
      position: relative;
      display: flex;
      align-items: flex-start;
      background-image: url('../../assets/img/column/202501_07.jpg');
      background-size: cover;
      min-height: 600px;
      padding: 60px;
    }
    .layer_02 img,.layer_03 img,.layer_04 img {
      display: none; /* 画像は背景として使用するので非表示にします */
    }
    .layer_02 .layer-text,.layer_03 .layer-text,.layer_04 .layer-text {
      padding: 15px;
      max-width: 45%; /* 文字が画像からはみ出さないように最大幅を設定 */
      word-wrap: break-word; /* 長い単語が折り返されるようにする */
      color: #424242;
      background-color: #fff;
    }
}
@media screen and (max-width: 699px) {
    .img-top-slide img {
    object-fit: contain;
    }
    .img-top-slide_text h2 {
      font-size: inherit;
    }
    .img-top-slide_text h2 span {
      font-size: .8em;
    }
    .img-top-slide_text {
      left: 10px;
    }
}
@media screen and (min-width: 769px) {
    .img-top-slide img {
    object-fit: contain;
    }
   .feature-row_item_text_right {
    padding-left: 60px;
   }
   .feature-row_item_text_left {
    padding-right: 60px;
   }
   .w-40 {
    width: 40%;
   }
   .w-60 {
    width: 60%;
   }
   .feature-row.step .step-item {
    width: 33.33%;
   }
   .img-top-slide img {
    height: 650px;
   }
}
@media screen and (min-width: 1px) and (max-width: 768px) {  
  .img-top-slide img {
  object-fit: contain;
  }
  .column_content h2 {
    margin-bottom: 40px;
  }
  .feature-row {
    flex-direction: column;
  }
  .feature-row_item_image {
    order: 1;
    margin-bottom: 15px;
  }
  .feature-row_item_text {
    order: 2;
  }
  .feature-row_voice_item, .feature-row_product_item, .feature-row_column_item {
      width: 100% !important;
  }
  .w-50, .w-25, .w-75 {
    width: 100% !important;
  }
  .layer-text {
    font-size: .8em;
  }
  .layer_01 .layer-text .fs-2 {
    font-size: 1.5em;
  }
  .summary {
    width: 100%; 
  }
}
</style>

<script>
import $ from "jquery";

$(function() {
 var topBtn = $('#page-top');    
 topBtn.hide();
 //スクロールが500に達したらボタン表示
 $(window).scroll(function () {
 if ($(this).scrollTop() > 500) {
 topBtn.fadeIn();
        } else {
 topBtn.fadeOut();
        }
    });
    
});

// ページ内リンク
export default{
  data: () => {
    return {isActive: []}
  },
  mounted (){
    window.scroll(0, 0);
    for(let i = 0; i < 2; i++) {
      this.isActive[i] = false
    }
  },
  methods: {
    scrollToAnchorPoint(refName) {
      const el = this.$refs[refName]
      el.scrollIntoView({ behavior: 'smooth'})
    },
    openInfo(ref) {
      console.log(ref)
      this.isActive[ref] = true
      console.log(this.isActive[ref])
    },
    closeInfo(ref) {
      console.log(ref)
      this.isActive[ref] = false
      console.log(this.isActive[ref])
    },
    scrollTop(){
      window.scrollTo({
      top: 0,
      behavior: "smooth"
      })
    },
    scrollTo(selector) {
     const el = document.querySelector(selector);
      if (el) {
        // 画面を対象の要素までスムーズにスクロールさせる
        el.scrollIntoView({ behavior: 'smooth' });
      }
    }
  }
}

</script>